import React, { useState } from 'react';
import { X, Download, Search, Check, Globe, Shield, Sparkles } from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firestore';

interface ImportTosdrModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TosdrService {
  id: number;
  name: string;
  domain: string;
  category: string;
  policies: {
    terms_of_service?: string;
    privacy_policy?: string;
  };
}

const POPULAR_SERVICES: TosdrService[] = [
  {
    id: 297,
    name: 'GitHub',
    domain: 'github.com',
    category: 'Developer Tools',
    policies: {
      terms_of_service: 'https://docs.github.com/en/site-policy/github-terms/github-terms-of-service',
      privacy_policy: 'https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement',
    },
  },
  {
    id: 194,
    name: 'Reddit',
    domain: 'reddit.com',
    category: 'Social Media',
    policies: {
      terms_of_service: 'https://www.redditinc.com/policies/user-agreement',
      privacy_policy: 'https://www.reddit.com/policies/privacy-policy',
    },
  },
  {
    id: 536,
    name: 'Discord',
    domain: 'discord.com',
    category: 'Social Media',
    policies: {
      terms_of_service: 'https://discord.com/terms',
      privacy_policy: 'https://discord.com/privacy',
    },
  },
  {
    id: 225,
    name: 'Spotify',
    domain: 'spotify.com',
    category: 'Media & Streaming',
    policies: {
      terms_of_service: 'https://www.spotify.com/us/legal/end-user-agreement/',
      privacy_policy: 'https://www.spotify.com/us/legal/privacy-policy/',
    },
  },
  {
    id: 206,
    name: 'Slack',
    domain: 'slack.com',
    category: 'SaaS & Productivity',
    policies: {
      terms_of_service: 'https://slack.com/terms-of-service',
      privacy_policy: 'https://slack.com/privacy-policy',
    },
  },
  {
    id: 222,
    name: 'DuckDuckGo',
    domain: 'duckduckgo.com',
    category: 'Cloud & Search',
    policies: {
      terms_of_service: 'https://duckduckgo.com/terms',
      privacy_policy: 'https://duckduckgo.com/privacy',
    },
  },
  {
    id: 182,
    name: 'Dropbox',
    domain: 'dropbox.com',
    category: 'SaaS & Productivity',
    policies: {
      terms_of_service: 'https://www.dropbox.com/terms',
      privacy_policy: 'https://www.dropbox.com/privacy',
    },
  },
  {
    id: 213,
    name: 'Cloudflare',
    domain: 'cloudflare.com',
    category: 'Developer Tools',
    policies: {
      terms_of_service: 'https://www.cloudflare.com/terms/',
      privacy_policy: 'https://www.cloudflare.com/privacypolicy/',
    },
  },
  {
    id: 216,
    name: 'Stripe',
    domain: 'stripe.com',
    category: 'FinTech & Payments',
    policies: {
      terms_of_service: 'https://stripe.com/legal/ssa',
      privacy_policy: 'https://stripe.com/privacy',
    },
  },
  {
    id: 305,
    name: 'Zoom',
    domain: 'zoom.us',
    category: 'SaaS & Productivity',
    policies: {
      terms_of_service: 'https://explore.zoom.us/en/terms/',
      privacy_policy: 'https://explore.zoom.us/en/privacy/',
    },
  },
];

export const ImportTosdrModal: React.FC<ImportTosdrModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([297, 194, 536, 225, 206, 222]);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [importing, setImporting] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const filteredServices = POPULAR_SERVICES.filter(
    (s) =>
      s.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      s.domain.toLowerCase().includes(searchFilter.toLowerCase()) ||
      s.category.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const handleBulkImport = async () => {
    if (!db) {
      alert('Firestore is not initialized.');
      return;
    }

    setImporting(true);
    setStatusMessage('Importing selected companies into Cloud Firestore...');

    try {
      const selectedServices = POPULAR_SERVICES.filter((s) => selectedIds.includes(s.id));
      for (const service of selectedServices) {
        const companyId = service.name.toLowerCase().replace(/[^a-z0-9_-]/g, '');
        const companyRef = doc(db, 'companies', companyId);

        const trackedPolicies: Record<string, any> = {};
        if (service.policies.terms_of_service) {
          trackedPolicies['terms_of_service'] = {
            name: `${service.name} Terms of Service`,
            url: service.policies.terms_of_service,
            active: true,
          };
          const policyRef = doc(db, 'companies', companyId, 'policies', 'terms_of_service');
          await setDoc(policyRef, {
            id: 'terms_of_service',
            company_id: companyId,
            name: `${service.name} Terms of Service`,
            url: service.policies.terms_of_service,
            status: 'active',
            updated_at: new Date().toISOString(),
          }, { merge: true });
        }

        if (service.policies.privacy_policy) {
          trackedPolicies['privacy_policy'] = {
            name: `${service.name} Privacy Policy`,
            url: service.policies.privacy_policy,
            active: true,
          };
          const policyRef = doc(db, 'companies', companyId, 'policies', 'privacy_policy');
          await setDoc(policyRef, {
            id: 'privacy_policy',
            company_id: companyId,
            name: `${service.name} Privacy Policy`,
            url: service.policies.privacy_policy,
            status: 'active',
            updated_at: new Date().toISOString(),
          }, { merge: true });
        }

        await setDoc(companyRef, {
          id: companyId,
          name: service.name,
          domain: service.domain,
          category: service.category,
          logo_url: `https://${service.domain}/favicon.ico`,
          description: `${service.name} tracked legal policies imported from ToS;DR.`,
          tracked_policies: trackedPolicies,
          status: 'active',
          updated_at: new Date().toISOString(),
        }, { merge: true });
      }

      setStatusMessage(`Successfully imported ${selectedServices.length} companies!`);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 800);
    } catch (err: any) {
      console.error('Import failed:', err);
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <Download className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Import from ToS;DR Directory
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Select verified tech companies and policies to monitor in your tracker
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search filter */}
        <div className="p-4 border-b border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-900/50">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search companies, domains, or categories..."
              className="w-full pl-9 pr-3 py-2 text-xs rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Company Selection List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
          {filteredServices.map((service) => {
            const isSelected = selectedIds.includes(service.id);
            return (
              <div
                key={service.id}
                onClick={() => toggleSelect(service.id)}
                className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                  isSelected
                    ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30'
                    : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700'
                    }`}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-900 dark:text-white">
                        {service.name}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {service.domain}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] px-2 py-0.2 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-medium">
                        {service.category}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {Object.keys(service.policies).length} policies configured
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-slate-400">
                  <Globe className="w-3.5 h-3.5" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Controls */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {statusMessage || `${selectedIds.length} of ${POPULAR_SERVICES.length} companies selected`}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={importing}
              className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkImport}
              disabled={importing || selectedIds.length === 0}
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-xs flex items-center gap-1.5"
            >
              {importing ? (
                <span>Importing...</span>
              ) : (
                <>
                  <Download className="w-3.5 h-3.5" />
                  Import Selected ({selectedIds.length})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
